import React from 'react';
import { Tooltip } from 'antd';
import { sparkToast } from '@spark';
import { v4 as uuidv4 } from 'uuid';

export async function handleCopy(text) {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      sparkToast.success('Copied to clipboard!');
    } catch (err) {
      sparkToast.error('Failed to copy content');
      console.error('Copy failed:', err);
    }
  } else {
    // Fallback for insecure context or unsupported browsers
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed'; // Prevent scrolling to bottom
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
      sparkToast.success('Copied to clipboard!');
    } catch (err) {
      sparkToast.error('Failed to copy content');
      console.error('Fallback copy failed:', err);
    }
    document.body.removeChild(textarea);
  }
}


export function getLanguageName(code) {
  const language = new Intl.DisplayNames(['en'], { type: 'language' });
  return language.of(code) || 'Unknown Language';
}

export function createdByUserToSearchText(createdByUser) {
  if (!createdByUser) return '';
  return [
    createdByUser.name,
    createdByUser.email,
    createdByUser.sub,
  ].filter(Boolean).join(' ');
}

export function taskLanguagesToSearchText(taskLanguages) {
  if (!Array.isArray(taskLanguages) || taskLanguages.length === 0) return '';
  const codes = taskLanguages.filter(Boolean);
  const names = codes.map((c) => {
    try {
      return getLanguageName(c);
    } catch {
      return '';
    }
  }).filter(Boolean);
  // Include both codes + display names so users can search either.
  return [...names, ...codes].join(' ');
}

export function normalizeSearchTerm(term) {
  if (term === null || term === undefined) return '';
  return String(term).trim().toLowerCase();
}

function flattenSearchParts(parts) {
  const out = [];
  const stack = [...parts];
  while (stack.length) {
    const item = stack.shift();
    if (item === null || item === undefined || item === '') continue;
    if (Array.isArray(item)) {
      stack.unshift(...item);
      continue;
    }
    out.push(item);
  }
  return out;
}

export function includesSearchTerm(searchTerm, ...parts) {
  const term = normalizeSearchTerm(searchTerm);
  if (!term) return true;
  const text = flattenSearchParts(parts)
    .map((v) => (typeof v === 'string' ? v : String(v)))
    .join(' ')
    .toLowerCase();
  return text.includes(term);
}

export function formatSize(numChars) {
  // Assume each character is 1 byte
  const bytes = numChars;

  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;

  let size = bytes;

  while (size >= 1024 && i < sizes.length - 1) {
    size /= 1024;
    i++;
  }

  return `${Math.round(size)}${sizes[i]}`;
}

export function formatDateTime(input) {
  if (!input) return '';

  const toDate = (val) => {
    if (val instanceof Date) return new Date(val.getTime());

    // Epoch numbers or numeric strings
    if (typeof val === 'number' || /^\d+$/.test(String(val))) {
      const n = Number(val);
      return new Date(n < 1e12 ? n * 1000 : n); // seconds vs millis
    }

    // Normalize common variants to ISO-ish
    let s = String(val).trim()
      .replace(' ', 'T')
      .replace(/(\d{4}-\d{2}-\d{2})-(\d{2}:\d{2}:\d{2}(?:\.\d+)?)/, '$1T$2')
      .replace(/\.(\d{3})\d+/, '.$1');

    // If string already has timezone, trust it
    if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s);

    // Otherwise choose the interpretation (local vs UTC) closest to now, avoiding future times
    const local = new Date(s);
    const utc = new Date(s + 'Z');
    const now = Date.now();
    const pick = (a, b) => {
      const future = (d) => d.getTime() - now > 2 * 60 * 1000;
      if (future(a) && !future(b)) return b;
      if (future(b) && !future(a)) return a;
      return Math.abs(now - a) <= Math.abs(now - b) ? a : b;
    };
    return pick(local, utc);
  };

  const d = toDate(input);
  if (isNaN(d)) return String(input);

  const datePart = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', second: 'numeric' });
  return `${datePart} at ${timePart}`;
}

export function formatDate(input) {
  const full = formatDateTime(input);
  if (!full) return '';
  const idx = full.indexOf(' at ');
  return idx === -1 ? full : full.slice(0, idx);
}

export function formatTime(input) {
  const full = formatDateTime(input);
  if (!full) return '';
  const idx = full.indexOf(' at ');
  return idx === -1 ? '' : full.slice(idx + 4);
}

/**
 * Renders the date as text; on hover shows only the time.
 * Use for "Last updated" / "Created At" to avoid duplication.
 */
export function DateTimeWithTimeTooltip({ value, emptyPlaceholder = '—' }) {
  const dateStr = formatDate(value);
  const timeStr = formatTime(value);
  if (!dateStr) return emptyPlaceholder;
  if (!timeStr) return dateStr;
  return (
    <Tooltip title={timeStr} placement="top">
      <span>{dateStr}</span>
    </Tooltip>
  );
}

// Add the color prediction function to be shared across components
export const predictionColors = [

  '#219653', // Green
  '#6772E5', // Indigo (light purple)
  '#eb2f96', // Pink
  '#a68c3d', // Yellow
  '#00C4B4', // Teal
  '#8E24AA', // Deep Purple
  '#2F80ED', // Blue
];

/**
 * Generates a consistent color based on input text
 * Used by Playground output renderers (OutputCell) and
 * LabelManagementModal so the same label always maps to the same swatch.
 * @param {string} text - The text to generate a color for
 * @returns {string} The hex color code
 */
export const getPredictionColor = (currentText, allText) => {
  allText = Array.from(new Set(allText))?.sort((a, b) => a.localeCompare(b));
  const colorList = ["blue", "green", "pink", "purple", "cyan", "magenta", "geekblue", "gold"];

  // Check if currentText is in allText
  const indexInAllText = allText?.indexOf(currentText);
  if (indexInAllText !== -1) {
    return colorList[indexInAllText % colorList.length];
  }

  // Create a combined string of currentText and allText
  const combinedText = currentText + allText.join("");

  // Simple hash function for string
  let hash = 0;
  for (let i = 0; i < combinedText.length; i++) {
    hash = (hash * 31 + combinedText.charCodeAt(i)) >>> 0;
  }

  // Get positive index
  const index = Math.abs(hash) % colorList.length;
  return colorList[index];
};

export function unwrapMarkdownCodeFence(rawText) {
  if (typeof rawText !== 'string') return rawText;
  const text = rawText.trim();
  if (!text) return text;

  // Prefer extracting a fenced code block (```json ... ```) when present.
  // Some models include multiple code blocks; pick the first one that looks like JSON.
  const fenceRegex = /```([a-zA-Z0-9_-]+)?[^\S\r\n]*\r?\n?([\s\S]*?)```/g;
  let best = null;
  let match;
  while ((match = fenceRegex.exec(text)) !== null) {
    const lang = (match[1] || '').toLowerCase();
    const payload = (match[2] || '').trim();
    if (!payload) continue;

    const looksJson = payload.startsWith('{') || payload.startsWith('[');
    if (looksJson && (!best || best.lang !== 'json')) {
      best = { lang, payload };
      if (lang === 'json') break;
      continue;
    }

    // If explicitly tagged as json, accept even if it doesn't start with {/[ (some outputs prefix whitespace)
    if (!best && lang === 'json') {
      best = { lang, payload };
    }
  }

  if (best?.payload) {
    return best.payload;
  }

  // Fallback: handle the case where the whole string is wrapped by fences without a newline after the language tag.
  if (text.startsWith('```') && text.endsWith('```')) {
    let inner = text.slice(3, -3).trim();
    inner = inner.replace(/^[a-zA-Z0-9_-]+\s*\r?\n/, '');
    return inner.trim();
  }

  return text;
}

function findMatchingBracketEnd(text, startIndex, openChar, closeChar) {
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = startIndex; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (escaped) {
        escaped = false;
        continue;
      }
      if (ch === '\\') {
        escaped = true;
        continue;
      }
      if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === openChar) {
      depth += 1;
      continue;
    }
    if (ch === closeChar) {
      depth -= 1;
      if (depth === 0) {
        return i;
      }
    }
  }
  return -1;
}

export function extractFirstJsonSubstring(rawText) {
  if (typeof rawText !== 'string') return rawText;
  const text = unwrapMarkdownCodeFence(rawText);
  if (!text) return text;

  const firstObj = text.indexOf('{');
  const firstArr = text.indexOf('[');
  const startIndex = firstObj === -1
    ? firstArr
    : firstArr === -1
      ? firstObj
      : Math.min(firstObj, firstArr);

  if (startIndex === -1) return text;

  const openChar = text[startIndex];
  const closeChar = openChar === '{' ? '}' : ']';
  const endIndex = findMatchingBracketEnd(text, startIndex, openChar, closeChar);
  if (endIndex === -1) {
    // Best-effort fallback: trim to the last closing bracket of the same type
    const lastIndex = text.lastIndexOf(closeChar);
    if (lastIndex > startIndex) {
      return text.slice(startIndex, lastIndex + 1).trim();
    }
    return text;
  }
  return text.slice(startIndex, endIndex + 1).trim();
}

// Models sometimes emit typographic ("smart") quotes instead of ASCII ones,
// which breaks JSON.parse even though the output looks like valid JSON.
function normalizeTypographicQuotes(text) {
  if (typeof text !== 'string') return text;
  return text
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'");
}

export function parseJsonLenient(rawText) {
  const cleaned = unwrapMarkdownCodeFence(rawText);
  const tryParse = (s) => JSON.parse(typeof s === 'string' ? s.trim() : s);

  const parseWithDoubleDecode = (text) => {
    let result = tryParse(text);
    // Some teachers return the JSON payload double-encoded as a string.
    if (typeof result === 'string') {
      try {
        result = tryParse(result);
      } catch {
        // keep first parse
      }
    }
    return result;
  };

  const parseWithRecovery = (text) => {
    try {
      return parseWithDoubleDecode(text);
    } catch (err) {
      // If there is extra text around the JSON, try extracting the first complete object/array.
      const candidate = extractFirstJsonSubstring(text);
      if (typeof candidate === 'string' && candidate !== text) {
        return parseWithDoubleDecode(candidate);
      }
      throw err;
    }
  };

  try {
    return parseWithRecovery(cleaned);
  } catch (err) {
    // Fallback only: valid JSON containing typographic quotes inside string
    // values never reaches this point, so normalization can't corrupt it.
    const normalized = normalizeTypographicQuotes(cleaned);
    if (normalized !== cleaned) {
      try {
        return parseWithRecovery(normalized);
      } catch {
        throw err;
      }
    }
    throw err;
  }
}

/**
 * True when `parseJsonLenient` can turn rawText into a JSON object or array.
 * Primitive results (bare strings/numbers) don't count: plain-text model
 * output must not be treated as JSON. This is the single source of truth for
 * "is this model output JSON?" so detection always matches what the JSON
 * viewer can actually parse.
 */
export function isParseableJsonStructure(rawText) {
  if (typeof rawText !== 'string' || !rawText.trim()) return false;
  try {
    const parsed = parseJsonLenient(rawText);
    return typeof parsed === 'object' && parsed !== null;
  } catch {
    return false;
  }
}

// Function to convert data to CSV format
export const exportTableToCSV = (data, columns, filename = 'export.csv') => {
  if (!data || data.length === 0) return;

  // Get visible columns and their titles
  const visibleColumns = columns.filter(col => !col.hidden);
  const headers = visibleColumns.map(col => col.title);
  const dataKeys = visibleColumns.map(col => col.dataIndex);

  // Create CSV content
  const csvRows = [];

  // Add headers
  csvRows.push(headers.join(','));

  // Add data rows
  data.forEach(row => {
    const values = dataKeys.map(key => {
      const value = row[key];
      // Handle special cases like null, undefined, and strings with commas
      if (value === null || value === undefined) return '';
      if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
        // Escape quotes and wrap in quotes if contains special characters
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    });
    csvRows.push(values.join(','));
  });

  const csvString = csvRows.join('\n');
  const blob = new Blob([csvString], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.setAttribute('hidden', '');
  a.setAttribute('href', url);
  a.setAttribute('download', filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export function getOrCreateClientUUID() {
  const clientId = localStorage.getItem('clientId');
  if (!clientId) {
    const newClientId = uuidv4();
    localStorage.setItem('clientId', newClientId);
    return newClientId;
  }
  console.log('the clientId is:', clientId);
  return clientId;
}

// Tenant/environment detection helper
export function isLagunaEnv() {
  console.log('window.env?.CUSTOMER_NAME:', window.env?.CUSTOMER_NAME);
  console.log('window.env:', window.env);
  const customer = window.env?.CUSTOMER_NAME?.toLowerCase();
  return !!customer?.includes('laguna');
}
